import {ApolloLink, Observable} from 'apollo-link';

// `setImmediate` is a Node-only API - it's not defined in browsers or
// jsdom.  Use a microtask fallback so we still defer the unsubscribe
// past the current synchronous callstack.
const deferUnsubscribe =
  typeof setImmediate === 'function'
    ? setImmediate
    : cb => Promise.resolve().then(cb);

// A custom link that will ensure requests are submitted in a sequence, not in parallel
// - if a request has a context key "serializeRequest" set to true, it will always be serialized
// - if a request has a context key "serializeRequest" set to false, or enableByDefault is false,
// it won't be serialized
// - queries are never serialized, unless they have a "serializeRequest" context key set to true
class SerializingLink extends ApolloLink {
  constructor(enableByDefault) {
    super();
    this.previous = null;
    this.enableByDefault = enableByDefault;
  }

  request(operation, forward) {
    // console.log('Operation entering SerializingLink', operation.toKey())
    if (!this.shouldSerializeRequest(operation)) {
      return forward(operation);
    }
    const previous = this.previous;
    // console.log('Waiting to fire...')
    const subscribers = [];
    // observable that will be subcribed to only once
    // we have to be careful not to accidentally subscribe more than once to the result of the
    // operation, because each time we subscribe it will re-send the request
    // likewise, we use the singleObserver as a proxy so that other requests won't subscribe more
    // than once to it
    const singleObserver = new Observable(observer => {
      // console.log('in single observer')
      const subs = [];
      if (!previous) {
        // console.log('firing immediately')
        subs.push(forward(operation).subscribe(observer));
      } else {
        // Release the queue whether the previous op succeeded OR failed.
        // If the previous op errors (e.g. network timeout, 5xx) we still
        // want subsequent mutations to run - otherwise one failed save
        // would permanently lock the UI.  The previous op's error is
        // already delivered to its own subscriber via the outer
        // singleObserver in that request's link chain; we deliberately
        // do NOT propagate it into this operation.
        let released = false;
        const releaseQueue = () => {
          if (released) return;
          released = true;
          // console.log('Previous done, firing new operation', operation.toKey())
          subs.push(forward(operation).subscribe(observer));
        };
        const prevSub = previous.subscribe({
          next() {
            // not interested
          },
          error() {
            releaseQueue();
          },
          complete() {
            releaseQueue();
          }
        });
        subs.push(prevSub);
      }
      return () => {
        subs.forEach(s => s.unsubscribe());
      };
    });
    // `completed` is true once the underlying operation has either
    // succeeded (complete) OR failed (error).  Both outcomes must
    // release anyone waiting in the queue - otherwise a single failed
    // mutation permanently locks the UI ("cells locked" bug).  We
    // deliberately do NOT propagate the error into the next operation's
    // subscriber; the error is delivered to this op's own subscribers
    // via the fan-out below and the next op is free to try its own call.
    let completed = false;
    const subscription = singleObserver.subscribe({
      next(result) {
        subscribers.forEach(s => s.next(result));
      },
      error(err) {
        subscribers.forEach(s => s.error(err));
        // defer in case the operation errors synchronously during subscribe()
        deferUnsubscribe(() => subscription && subscription.unsubscribe());
        completed = true;
      },
      complete() {
        subscribers.forEach(s => s.complete());
        // defer in case the operation completes synchronously during subscribe()
        deferUnsubscribe(() => subscription && subscription.unsubscribe());
        completed = true;
      }
    });
    // this is the one that people will subscribe to.  If the operation
    // already finished (success or failure) before a late subscriber
    // joined, fire complete() so the serializing queue advances.
    const sharedObserver = new Observable(observer => {
      // console.log('running shared observer subscription')
      if (completed) {
        observer.complete();
      } else {
        subscribers.push(observer);
      }
    });
    this.previous = sharedObserver;
    return sharedObserver;
  }

  shouldSerializeRequest(operation) {
    if (operation.getContext().serializeRequest === true) return true;
    if (operation.getContext().serializeRequest === false) return false;
    for (let def of operation.query.definitions) {
      if (def.kind === 'OperationDefinition' && def.operation === 'mutation')
        return this.enableByDefault;
    }
    // query?
    return false;
  }
}

export default SerializingLink;
